import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { randomUUID } from '@/utils/uuid';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';

import { AnalisisDatosGeneralesForm } from '@/features/analisis/common/components/AnalisisDatosGeneralesForm';
import {
  useBaermannListByProtocolo,
  useCreateBaermann,
  useDeleteBaermann,
  useUpdateBaermann,
} from '@/features/analisis/baermann/hooks/useBaermann';
import {
  useCreateCoprocultivo,
  useCoprocultivoListByProtocolo,
  useDeleteCoprocultivo,
  useUpdateCoprocultivo,
} from '@/features/analisis/coprocultivo/hooks/useCoprocultivo';
import {
  useCreateOpg,
  useDeleteOpg,
  useOpgListByProtocolo,
  useUpdateOpg,
} from '@/features/analisis/opg/hooks/useOpg';
import {
  useCreateSedimentacionFh,
  useDeleteSedimentacionFh,
  useEditarCaptionFotoSedimentacionFh,
  useEliminarFotoSedimentacionFh,
  useSedimentacionFhListByProtocolo,
  useUpdateSedimentacionFh,
  useUploadFotoSedimentacionFh,
} from '@/features/analisis/sedimentacion-fh/hooks/useSedimentacionFh';
import { fotosTemporalesApi } from '@/features/analisis/common/api/fotosTemporalesApi';
import { FotosManager } from '@/features/analisis/common/components/FotosManager';
import type { FotoTemporal } from '@/features/analisis/common/fotosTypes';
import { useNotify } from '@/hooks/useNotify';
import { getErrorMessage, isApiError } from '@/lib/apiError';
import { TipoAnalisis } from '@/types/enums';

import { formatNumeroAnalisis } from '../../utils/formatNumeroAnalisis';
import { useCreateHpg, useHpg, useUpdateHpg } from '../hooks/useHpg';
import type { SedFhComplementarioDraft } from '../hooks/useHpgDraft';
import { useHpgDraft } from '../hooks/useHpgDraft';
import type { HpgDraft } from '../hooks/useHpgDraft';
import { esMuestraVacia, hpgFormSchema } from '../schemas';
import type { HpgFormValues, HpgMuestraFormValues } from '../schemas';
import type { CoprocultivoMuestra } from '../../coprocultivo/types';
import { HpgDialogBaermannTab } from './HpgDialogBaermannTab';
import { HpgDialogCoprocultivoTab } from './HpgDialogCoprocultivoTab';
import { HpgDialogOpgTab } from './HpgDialogOpgTab';
import { HpgDialogSedFhTab } from './HpgDialogSedFhTab';
import { HpgFormMuestrasTable } from './HpgFormMuestrasTable';
import type {
  BaermannComplementarioState,
  ComplementarioState,
  CoprocultivoComplementarioState,
  ErrorReferencia,
  PosicionHpgValida,
  SedFhComplementarioState,
  TipoComplementario,
} from './hpgDialogTypes';

type TabId = 'datos' | 'muestras' | 'agregar' | `comp-${string}` | `fotos-${string}`;

const TIPO_LABEL: Record<TipoComplementario, string> = {
  opg: 'OPG',
  coprocultivo: 'Coprocultivo',
  baermann: 'Baermann',
  sedimentacion_fh: 'Sed. Fh',
};

const DATOS_FIELDS = [
  'fecha_analisis',
  'identificacion_muestras',
  'alimentacion',
  'ultimo_antiparasitario',
  'fecha_ultimo_tratamiento',
  'comentarios_lote',
] as const;

const DEFAULT_MUESTRA: HpgMuestraFormValues = {
  identificacion: null,
  valor: null,
  observacion: null,
};

function makeMuestrasIniciales(): HpgMuestraFormValues[] {
  return Array.from({ length: 10 }, () => ({ ...DEFAULT_MUESTRA }));
}

// ── Pure validation helpers (module-level) ────────────────────────────────────

function limpiarCoprocultivos(
  comps: ComplementarioState[],
  numerosValidos: Set<number>,
): ComplementarioState[] {
  return comps.map((comp) => {
    if (comp.tipo !== 'coprocultivo' || comp.marcadoParaEliminar) return comp;
    const cleanedRefs = comp.numerosHpgReferencia.filter((n) => numerosValidos.has(n));
    if (cleanedRefs.length === comp.numerosHpgReferencia.length) return comp;
    return { ...comp, numerosHpgReferencia: cleanedRefs, isDirty: true } as ComplementarioState;
  });
}

function validarReferencias(
  comps: ComplementarioState[],
  numerosValidos: Set<number>,
): ErrorReferencia[] {
  const errores: ErrorReferencia[] = [];
  let visibleIndex = 0;

  for (const comp of comps) {
    if (comp.marcadoParaEliminar) continue;
    const tabId = `comp-${comp.localId}`;
    const tabLabel = `${TIPO_LABEL[comp.tipo]} ${visibleIndex + 1}`;
    visibleIndex++;

    if (comp.tipo === 'opg') {
      if (comp.muestras.length === 0) {
        errores.push({
          tabId,
          tabLabel,
          mensaje: `${tabLabel} no tiene muestras. Eliminá el complementario si no hay datos.`,
        });
      } else {
        comp.muestras.forEach((m, i) => {
          const ref = m.numero_muestra_hpg_referencia;
          if (ref !== null && !numerosValidos.has(ref)) {
            errores.push({
              tabId,
              tabLabel,
              mensaje: `La muestra ${i + 1} de ${tabLabel} referencia la muestra HPG ${ref} que ya no existe.`,
            });
          }
        });
      }
    }

    if (comp.tipo === 'baermann' || comp.tipo === 'sedimentacion_fh') {
      const maxTotal = comp.tipo === 'baermann' ? 15 : 25;
      let totalMuestras = 0;

      comp.pools.forEach((pool, j) => {
        totalMuestras += pool.numeros_muestras.length;
        if (pool.numeros_muestras.length === 0) {
          errores.push({
            tabId,
            tabLabel,
            mensaje: `El pool ${j + 1} de ${tabLabel} no tiene muestras HPG vinculadas.`,
          });
        } else {
          const invalidas = pool.numeros_muestras.filter((n) => !numerosValidos.has(n));
          if (invalidas.length > 0) {
            errores.push({
              tabId,
              tabLabel,
              mensaje: `El pool ${j + 1} de ${tabLabel} referencia las muestras HPG ${invalidas.join(', ')} que ya no existen.`,
            });
          }
        }
        if (pool.valor === null) {
          errores.push({
            tabId,
            tabLabel,
            mensaje: `El pool ${j + 1} de ${tabLabel} no tiene resultado asignado (Positivo/Negativo).`,
          });
        }
      });

      if (totalMuestras > maxTotal) {
        errores.push({
          tabId,
          tabLabel,
          mensaje: `${tabLabel} supera el máximo de ${maxTotal} muestras totales (actual: ${totalMuestras}).`,
        });
      }
    }
  }

  return errores;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface HpgFormDialogProps {
  open: boolean;
  onClose: () => void;
  uidProtocolo: string;
  uidAnalisis?: string;
  numeroProtocolo?: number;
  establecimientoNombre?: string;
  onSuccess?: () => void;
}

export function HpgFormDialog({
  open,
  onClose,
  uidProtocolo,
  uidAnalisis,
  numeroProtocolo,
  establecimientoNombre,
  onSuccess,
}: HpgFormDialogProps): JSX.Element {
  const isEdit = !!uidAnalisis;
  const { saveDraft, loadDraft, clearDraft } = useHpgDraft();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const notify = useNotify();

  // ── HPG queries / mutations ──────────────────────────────────────────────
  const hpgQuery = useHpg(uidProtocolo, uidAnalisis);
  const createHpgMutation = useCreateHpg();
  const updateHpgMutation = useUpdateHpg();

  // ── Complementario queries (only active in edit mode) ────────────────────
  const uid = isEdit ? uidProtocolo : undefined;
  const opgQuery = useOpgListByProtocolo(uid);
  const coproQuery = useCoprocultivoListByProtocolo(uid);
  const baermannQuery = useBaermannListByProtocolo(uid);
  const sedFhQuery = useSedimentacionFhListByProtocolo(uid);

  // ── Complementario mutations ─────────────────────────────────────────────
  const createOpgMutation = useCreateOpg();
  const updateOpgMutation = useUpdateOpg();
  const deleteOpgMutation = useDeleteOpg();
  const createCoproMutation = useCreateCoprocultivo();
  const updateCoproMutation = useUpdateCoprocultivo();
  const deleteCoproMutation = useDeleteCoprocultivo();
  const createBaermannMutation = useCreateBaermann();
  const updateBaermannMutation = useUpdateBaermann();
  const deleteBaermannMutation = useDeleteBaermann();
  const createSedFhMutation = useCreateSedimentacionFh();
  const updateSedFhMutation = useUpdateSedimentacionFh();
  const deleteSedFhMutation = useDeleteSedimentacionFh();

  // ── HPG form ─────────────────────────────────────────────────────────────
  const methods = useForm<HpgFormValues>({
    resolver: zodResolver(hpgFormSchema),
    defaultValues: {
      fecha_analisis: new Date(),
      identificacion_muestras: null,
      alimentacion: null,
      ultimo_antiparasitario: null,
      fecha_ultimo_tratamiento: null,
      comentarios_lote: null,
      muestras: makeMuestrasIniciales(),
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty: hpgIsDirty, errors, submitCount },
  } = methods;

  const watchedMuestras = useWatch({ control: methods.control, name: 'muestras' });
  const watchedAll = useWatch({ control: methods.control });

  // ── UI state ─────────────────────────────────────────────────────────────
  const [tabActiva, setTabActiva] = useState<TabId>('datos');
  const [complementarios, setComplementarios] = useState<ComplementarioState[]>([]);
  const [tipoComplementario, setTipoComplementario] = useState<TipoComplementario | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmDeleteComp, setConfirmDeleteComp] = useState<string | null>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showLockedDialog, setShowLockedDialog] = useState(false);
  const [showPartialFailureDialog, setShowPartialFailureDialog] = useState(false);
  const [partialFailureErrors, setPartialFailureErrors] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [minMuestrasError, setMinMuestrasError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [erroresValidacion, setErroresValidacion] = useState<ErrorReferencia[]>([]);
  const [hasAttemptedValidacion, setHasAttemptedValidacion] = useState(false);
  const [pendingDraft, setPendingDraft] = useState<HpgDraft | null>(null);

  const initializedRef = useRef(false);
  const retryRef = useRef<{ uidHpg: string; hpgValues: HpgFormValues } | null>(null);

  // ── Derived ───────────────────────────────────────────────────────────────
  const posicionesHpgValidas = useMemo<PosicionHpgValida[]>(() => {
    const muestras = watchedMuestras ?? [];
    const result: PosicionHpgValida[] = [];
    muestras.forEach((m, i) => {
      if (!esMuestraVacia(m)) {
        result.push({ posicion: i + 1, valor: m.valor, identificacion: m.identificacion });
      }
    });
    return result;
  }, [watchedMuestras]);

  const complementariosVisibles = complementarios.filter((c) => !c.marcadoParaEliminar);

  const muestrasCount = watchedMuestras?.length ?? 0;

  const complementariosDirty = complementarios.some(
    (c) =>
      c.isDirty ||
      (c.marcadoParaEliminar && c.uidAnalisis !== null) ||
      (!c.uidAnalisis && !c.marcadoParaEliminar),
  );

  const hasUnsavedChanges = hpgIsDirty || complementariosDirty;

  // Tab error indicators (only after first submit attempt)
  const hasAttemptedSubmit = submitCount > 0;
  const tabDatosHasError = hasAttemptedSubmit && DATOS_FIELDS.some((f) => !!errors[f]);
  const tabMuestrasHasError = hasAttemptedSubmit && (!!errors.muestras || minMuestrasError);

  // ── Effects ───────────────────────────────────────────────────────────────

  // Reset initializedRef and validation state when dialog closes
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      setErroresValidacion([]);
      setHasAttemptedValidacion(false);
      setPendingDraft(null);
    }
  }, [open]);

  // Create mode: full reset + check for saved draft
  useEffect(() => {
    if (open && !isEdit) {
      reset({
        fecha_analisis: new Date(),
        identificacion_muestras: null,
        alimentacion: null,
        ultimo_antiparasitario: null,
        fecha_ultimo_tratamiento: null,
        comentarios_lote: null,
        muestras: makeMuestrasIniciales(),
      });
      setComplementarios([]);
      setTipoComplementario(null);
      setTabActiva('datos');
      setSubmitError(null);
      setMinMuestrasError(false);
      setErroresValidacion([]);
      setHasAttemptedValidacion(false);
      setPendingDraft(loadDraft(uidProtocolo));
    }
  }, [open, isEdit, reset]);

  // Edit mode: initialize form + complementarios once all queries resolve
  useEffect(() => {
    if (!open || !isEdit || !hpgQuery.data) return;
    if (opgQuery.isLoading || coproQuery.isLoading || baermannQuery.isLoading || sedFhQuery.isLoading) return;
    if (initializedRef.current) return;

    initializedRef.current = true;

    const hpg = hpgQuery.data;
    const uid = uidAnalisis!;

    reset({
      fecha_analisis: parseISO(hpg.fecha_analisis),
      identificacion_muestras: hpg.identificacion_muestras,
      alimentacion: hpg.alimentacion,
      ultimo_antiparasitario: hpg.ultimo_antiparasitario,
      fecha_ultimo_tratamiento: hpg.fecha_ultimo_tratamiento
        ? parseISO(hpg.fecha_ultimo_tratamiento)
        : null,
      comentarios_lote: hpg.comentarios_lote,
      muestras: hpg.muestras_hpg.length > 0 ? hpg.muestras_hpg : [DEFAULT_MUESTRA],
    });

    const opgs: ComplementarioState[] = (opgQuery.data ?? [])
      .filter((o) => o.uid_analisis_hpg_referencia === uid)
      .map((o) => ({
        tipo: 'opg' as const,
        localId: randomUUID(),
        uidAnalisis: o.uid_analisis,
        muestras: o.muestras_opg,
        marcadoParaEliminar: false,
        isDirty: false,
      }));

    const coprocs: ComplementarioState[] = (coproQuery.data ?? [])
      .filter((c) => c.uid_analisis_hpg_referencia === uid)
      .map((c) => {
        const m = c.muestras_coprocultivo[0];
        return {
          tipo: 'coprocultivo' as const,
          localId: randomUUID(),
          uidAnalisis: c.uid_analisis,
          numerosHpgReferencia: m?.numeros_muestras_hpg_referencia ?? [],
          porcentajeOstertagia: m?.porcentaje_ostertagia ?? null,
          porcentajeTrichostrongylus: m?.porcentaje_trichostrongylus ?? null,
          porcentajeCooperia: m?.porcentaje_cooperia ?? null,
          porcentajeHaemonchus: m?.porcentaje_haemonchus ?? null,
          porcentajeOesophagostomum: m?.porcentaje_oesophagostomum ?? null,
          porcentajeNematodirus: m?.porcentaje_nematodirus ?? null,
          observacion: m?.observacion ?? null,
          marcadoParaEliminar: false,
          isDirty: false,
          sumError: false,
        };
      });

    const baermanns: ComplementarioState[] = (baermannQuery.data ?? [])
      .filter((b) => b.uid_analisis_hpg_referencia === uid)
      .map((b) => ({
        tipo: 'baermann' as const,
        localId: randomUUID(),
        uidAnalisis: b.uid_analisis,
        pools: b.pools.map((p) => ({
          id: randomUUID(),
          numeros_muestras: p.numeros_muestras,
          valor: p.valor,
          observacion: p.observacion,
        })),
        marcadoParaEliminar: false,
        isDirty: false,
      }));

    const sedFhs: ComplementarioState[] = (sedFhQuery.data ?? [])
      .filter((s) => s.uid_analisis_hpg_referencia === uid)
      .map((s) => ({
        tipo: 'sedimentacion_fh' as const,
        localId: randomUUID(),
        uidAnalisis: s.uid_analisis,
        pools: s.pools.map((p) => ({
          id: randomUUID(),
          numeros_muestras: p.numeros_muestras,
          valor: p.valor,
          observacion: p.observacion,
        })),
        fotosTemporales: [],
        marcadoParaEliminar: false,
        isDirty: false,
      }));

    const todos = [...opgs, ...coprocs, ...baermanns, ...sedFhs];
    const tipos = [...new Set(todos.map((c) => c.tipo))];
    if (tipos.length > 1) {
      // eslint-disable-next-line no-console
      console.warn('[HpgFormDialog] complementarios de tipos mixtos (legacy):', tipos);
    }
    const tipoActivo = tipos[0] ?? null;
    const filtrados = tipoActivo ? todos.filter((c) => c.tipo === tipoActivo) : todos;

    setComplementarios(filtrados);
    setTipoComplementario(tipoActivo);
    setTabActiva('datos');
    setSubmitError(null);
    setMinMuestrasError(false);
    setErroresValidacion([]);
    setHasAttemptedValidacion(false);
  }, [
    open,
    isEdit,
    uidAnalisis,
    hpgQuery.data,
    opgQuery.data,
    coproQuery.data,
    baermannQuery.data,
    sedFhQuery.data,
    opgQuery.isLoading,
    coproQuery.isLoading,
    baermannQuery.isLoading,
    sedFhQuery.isLoading,
    reset,
  ]);

  // Auto-reset tipoComplementario when all complementarios are removed
  useEffect(() => {
    if (tipoComplementario === null) return;
    if (!complementarios.some((c) => !c.marcadoParaEliminar)) {
      setTipoComplementario(null);
    }
  }, [complementarios, tipoComplementario]);

  // Reactive validation: re-run after first save attempt on every relevant change
  useEffect(() => {
    if (!hasAttemptedValidacion) return;
    const numerosValidos = new Set(posicionesHpgValidas.map((p) => p.posicion));
    setErroresValidacion(validarReferencias(complementarios, numerosValidos));
  }, [complementarios, posicionesHpgValidas, hasAttemptedValidacion]);

  // Focus first valor field when switching to muestras tab
  useEffect(() => {
    if (tabActiva !== 'muestras') return;
    const timer = setTimeout(() => {
      document.querySelector<HTMLInputElement>('[data-valor-field="true"]')?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [tabActiva]);

  // Autosave draft while editing in creation mode
  useEffect(() => {
    if (!open || isEdit) return;
    if (!hpgIsDirty && !complementariosDirty) return;
    saveDraft(uidProtocolo, methods.getValues(), complementarios, tipoComplementario);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedAll, complementarios]);

  // Warn before tab close when there are unsaved changes
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  function patchComp(localId: string, patch: object): void {
    setComplementarios((prev) =>
      prev.map((c) => (c.localId === localId ? ({ ...c, ...patch } as ComplementarioState) : c)),
    );
  }

  async function handleRestoreDraft(): Promise<void> {
    if (!pendingDraft) return;
    const fv = pendingDraft.formValues;
    reset({
      fecha_analisis: parseISO(fv.fecha_analisis),
      identificacion_muestras: fv.identificacion_muestras,
      alimentacion: fv.alimentacion,
      ultimo_antiparasitario: fv.ultimo_antiparasitario,
      fecha_ultimo_tratamiento: fv.fecha_ultimo_tratamiento
        ? parseISO(fv.fecha_ultimo_tratamiento)
        : null,
      comentarios_lote: fv.comentarios_lote,
      muestras: fv.muestras,
    });
    const restoredComps = await Promise.all(
      pendingDraft.complementarios.map(async (c) => {
        if (c.tipo !== 'sedimentacion_fh') return c as ComplementarioState;
        const draft = c as SedFhComplementarioDraft;
        const fotos = await Promise.all(
          draft.fotosTemporales.map(async (f): Promise<FotoTemporal> => {
            try {
              const { presigned_url } = await fotosTemporalesApi.getFotoTemporal(f.uid_foto);
              return { localId: f.localId, uid_foto: f.uid_foto, blobUrl: presigned_url, file: new File([], '', { type: f.content_type }), caption: f.caption, uploadStatus: 'done' };
            } catch {
              return { localId: f.localId, uid_foto: f.uid_foto, blobUrl: '', file: new File([], '', { type: f.content_type }), caption: f.caption, uploadStatus: 'done' };
            }
          }),
        );
        return { ...draft, fotosTemporales: fotos } as ComplementarioState;
      }),
    );
    setComplementarios(restoredComps);
    setTipoComplementario(pendingDraft.tipoComplementario);
    setTabActiva('muestras');
    setPendingDraft(null);
  }

  function handleDiscardDraft(): void {
    clearDraft(uidProtocolo);
    setPendingDraft(null);
  }

  function agregarNuevo(tipo: TipoComplementario): void {
    const localId = randomUUID();
    let nuevo: ComplementarioState;

    switch (tipo) {
      case 'opg':
        nuevo = {
          tipo,
          localId,
          uidAnalisis: null,
          muestras: posicionesHpgValidas.map((p) => ({
            numero_muestra_hpg_referencia: p.posicion,
            identificacion: null,
            valor: null,
            observacion: null,
          })),
          marcadoParaEliminar: false,
          isDirty: true,
        };
        break;
      case 'coprocultivo':
        nuevo = {
          tipo,
          localId,
          uidAnalisis: null,
          numerosHpgReferencia: [],
          porcentajeOstertagia: null,
          porcentajeTrichostrongylus: null,
          porcentajeCooperia: null,
          porcentajeHaemonchus: null,
          porcentajeOesophagostomum: null,
          porcentajeNematodirus: null,
          observacion: null,
          marcadoParaEliminar: false,
          isDirty: true,
          sumError: false,
        };
        break;
      case 'baermann':
        nuevo = {
          tipo,
          localId,
          uidAnalisis: null,
          pools: [{ id: randomUUID(), numeros_muestras: [], valor: null, observacion: null }],
          marcadoParaEliminar: false,
          isDirty: true,
        };
        break;
      case 'sedimentacion_fh':
        nuevo = {
          tipo,
          localId,
          uidAnalisis: null,
          pools: [{ id: randomUUID(), numeros_muestras: [], valor: null, observacion: null }],
          fotosTemporales: [],
          marcadoParaEliminar: false,
          isDirty: true,
        };
        break;
    }

    setComplementarios((prev) => [...prev, nuevo]);
    setTipoComplementario(tipo);
    setMenuAnchor(null);
    setTabActiva(`comp-${localId}`);
  }

  function handleConfirmEliminarComp(): void {
    const localId = confirmDeleteComp;
    if (!localId) return;
    setConfirmDeleteComp(null);
    setComplementarios((prev) => {
      const comp = prev.find((c) => c.localId === localId);
      if (!comp) return prev;
      if (comp.uidAnalisis !== null) {
        return prev.map((c) =>
          c.localId === localId ? ({ ...c, marcadoParaEliminar: true } as ComplementarioState) : c,
        );
      }
      return prev.filter((c) => c.localId !== localId);
    });
    setTabActiva('datos');
  }

  // ── Validation helpers ────────────────────────────────────────────────────

  function checkCoprocultivoSums(): boolean {
    const coproErrors: { localId: string; hasError: boolean }[] = complementarios
      .filter((c): c is CoprocultivoComplementarioState => c.tipo === 'coprocultivo' && !c.marcadoParaEliminar)
      .map((c) => {
        const suma =
          (c.porcentajeOstertagia ?? 0) +
          (c.porcentajeTrichostrongylus ?? 0) +
          (c.porcentajeCooperia ?? 0) +
          (c.porcentajeHaemonchus ?? 0) +
          (c.porcentajeOesophagostomum ?? 0) +
          (c.porcentajeNematodirus ?? 0);
        return { localId: c.localId, hasError: suma !== 100 && suma !== 0 };
      });

    const firstError = coproErrors.find((e) => e.hasError);
    if (!firstError) return true;

    setComplementarios((prev) =>
      prev.map((c) => {
        if (c.tipo !== 'coprocultivo') return c;
        const entry = coproErrors.find((e) => e.localId === c.localId);
        if (!entry) return c;
        return { ...c, sumError: entry.hasError } as ComplementarioState;
      }),
    );
    setTabActiva(`comp-${firstError.localId}`);
    return false;
  }

  // ── Save helpers ──────────────────────────────────────────────────────────

  function buildPools(c: BaermannComplementarioState | SedFhComplementarioState) {
    return c.pools.map((p) => {
      if (p.valor === null) throw new Error('Pool sin resultado — invariante violado');
      return { numeros_muestras: p.numeros_muestras, valor: p.valor, observacion: p.observacion };
    });
  }

  function buildCoproMuestra(c: CoprocultivoComplementarioState, validPos: number[]): CoprocultivoMuestra {
    return {
      numeros_muestras_hpg_referencia: c.numerosHpgReferencia.filter((n) => validPos.includes(n)),
      porcentaje_ostertagia: c.porcentajeOstertagia ?? 0,
      porcentaje_trichostrongylus: c.porcentajeTrichostrongylus ?? 0,
      porcentaje_cooperia: c.porcentajeCooperia ?? 0,
      porcentaje_haemonchus: c.porcentajeHaemonchus ?? 0,
      porcentaje_oesophagostomum: c.porcentajeOesophagostomum ?? 0,
      porcentaje_nematodirus: c.porcentajeNematodirus ?? 0,
      observacion: c.observacion,
    };
  }

  async function doSaveComplementarios(uidHpg: string, hpgValues: HpgFormValues): Promise<void> {
    const validPos = hpgValues.muestras
      .map((m, i) => ({ posicion: i + 1, empty: esMuestraVacia(m) }))
      .filter((p) => !p.empty)
      .map((p) => p.posicion);

    const camposComunes = {
      fecha_analisis: hpgValues.fecha_analisis.toISOString(),
      identificacion_muestras: hpgValues.identificacion_muestras,
      alimentacion: hpgValues.alimentacion,
      ultimo_antiparasitario: hpgValues.ultimo_antiparasitario,
      fecha_ultimo_tratamiento: hpgValues.fecha_ultimo_tratamiento?.toISOString() ?? null,
      comentarios_lote: hpgValues.comentarios_lote,
    };

    const compsActivos = complementarios.filter((c) => !c.marcadoParaEliminar);
    const compsParaEliminar = complementarios.filter((c) => c.marcadoParaEliminar && c.uidAnalisis);
    const compsParaCrear = compsActivos.filter((c) => !c.uidAnalisis);
    const compsParaActualizar = compsActivos.filter((c) => c.uidAnalisis && c.isDirty);

    const ops: Promise<unknown>[] = [
      ...compsParaEliminar.map(async (c) => {
        const uid = c.uidAnalisis!;
        switch (c.tipo) {
          case 'opg':
            return deleteOpgMutation.mutateAsync({ uidProtocolo, uidAnalisis: uid });
          case 'coprocultivo':
            return deleteCoproMutation.mutateAsync({ uidProtocolo, uidAnalisis: uid });
          case 'baermann':
            return deleteBaermannMutation.mutateAsync({ uidProtocolo, uidAnalisis: uid });
          case 'sedimentacion_fh':
            return deleteSedFhMutation.mutateAsync({ uidProtocolo, uidAnalisis: uid });
        }
      }),
      ...compsParaCrear.map(async (c) => {
        switch (c.tipo) {
          case 'opg':
            return createOpgMutation.mutateAsync({
              uidProtocolo,
              data: {
                tipo_analisis: TipoAnalisis.OPG,
                ...camposComunes,
                muestras_opg: c.muestras,
                uid_analisis_hpg_referencia: uidHpg,
              },
            });
          case 'coprocultivo':
            return createCoproMutation.mutateAsync({
              uidProtocolo,
              data: {
                tipo_analisis: TipoAnalisis.COPROCULTIVO,
                ...camposComunes,
                muestras_coprocultivo: [buildCoproMuestra(c as CoprocultivoComplementarioState, validPos)],
                uid_analisis_hpg_referencia: uidHpg,
              },
            });
          case 'baermann':
            return createBaermannMutation.mutateAsync({
              uidProtocolo,
              data: {
                tipo_analisis: TipoAnalisis.BAERMANN,
                ...camposComunes,
                pools: buildPools(c),
                uid_analisis_hpg_referencia: uidHpg,
              },
            });
          case 'sedimentacion_fh': {
            const doneFotos = c.fotosTemporales.filter((f) => f.uploadStatus === 'done');
            return createSedFhMutation.mutateAsync({
              uidProtocolo,
              data: {
                tipo_analisis: TipoAnalisis.SEDIMENTACION_FH,
                ...camposComunes,
                pools: buildPools(c),
                fotos: doneFotos.map((f) => ({ uid_foto: f.uid_foto, content_type: f.file.type, caption: f.caption })),
                uid_analisis_hpg_referencia: uidHpg,
              },
            });
          }
        }
      }),
      ...compsParaActualizar.map(async (c) => {
        const uid = c.uidAnalisis!;
        switch (c.tipo) {
          case 'opg':
            return updateOpgMutation.mutateAsync({
              uidProtocolo,
              uidAnalisis: uid,
              data: { muestras_opg: c.muestras },
            });
          case 'coprocultivo':
            return updateCoproMutation.mutateAsync({
              uidProtocolo,
              uidAnalisis: uid,
              data: { muestras_coprocultivo: [buildCoproMuestra(c as CoprocultivoComplementarioState, validPos)] },
            });
          case 'baermann':
            return updateBaermannMutation.mutateAsync({
              uidProtocolo,
              uidAnalisis: uid,
              data: { pools: buildPools(c) },
            });
          case 'sedimentacion_fh':
            return updateSedFhMutation.mutateAsync({
              uidProtocolo,
              uidAnalisis: uid,
              data: {
                pools: buildPools(c),
              },
            });
        }
      }),
    ];

    const results = await Promise.allSettled(ops);
    const failures = results
      .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
      .map((r) => getErrorMessage(r.reason));

    if (failures.length > 0) {
      retryRef.current = { uidHpg, hpgValues };
      setPartialFailureErrors(failures);
      setShowPartialFailureDialog(true);
    } else {
      clearDraft(uidProtocolo);
      notify.success('Análisis guardado.');
      onSuccess?.();
      onClose();
    }
  }

  // ── Main save handler (called by RHF handleSubmit) ────────────────────────

  function handleSaveError(errs: FieldErrors<HpgFormValues>): void {
    const hasDatosErrors = DATOS_FIELDS.some((f) => !!errs[f]);
    const hasMuestrasErrors = !!errs.muestras;
    if (hasDatosErrors) {
      setTabActiva('datos');
    } else if (hasMuestrasErrors) {
      setTabActiva('muestras');
    }
  }

  async function handleSave(values: HpgFormValues): Promise<void> {
    setSubmitError(null);
    setMinMuestrasError(false);

    const muestrasParaEnviar = values.muestras.filter((m) => !esMuestraVacia(m));
    if (muestrasParaEnviar.length === 0) {
      setMinMuestrasError(true);
      setTabActiva('muestras');
      return;
    }

    // Silently clean broken coprocultivo references before validation
    const numerosValidos = new Set(posicionesHpgValidas.map((p) => p.posicion));
    const compsLimpios = limpiarCoprocultivos(complementarios, numerosValidos);
    setComplementarios(compsLimpios);

    // Cross-reference validation
    const errores = validarReferencias(compsLimpios, numerosValidos);
    setErroresValidacion(errores);
    setHasAttemptedValidacion(true);
    if (errores.length > 0) return;

    if (!checkCoprocultivoSums()) return;

    setIsSaving(true);
    try {
      const hpgBody = {
        fecha_analisis: values.fecha_analisis.toISOString(),
        identificacion_muestras: values.identificacion_muestras,
        alimentacion: values.alimentacion,
        ultimo_antiparasitario: values.ultimo_antiparasitario,
        fecha_ultimo_tratamiento: values.fecha_ultimo_tratamiento?.toISOString() ?? null,
        comentarios_lote: values.comentarios_lote,
        muestras_hpg: muestrasParaEnviar,
      };

      let uidHpg: string;
      if (isEdit && uidAnalisis) {
        await updateHpgMutation.mutateAsync({ uidProtocolo, uidAnalisis, data: hpgBody });
        uidHpg = uidAnalisis;
      } else {
        const created = await createHpgMutation.mutateAsync({
          uidProtocolo,
          data: { ...hpgBody, tipo_analisis: TipoAnalisis.HPG },
        });
        uidHpg = created.uid_analisis;
      }

      await doSaveComplementarios(uidHpg, values);
    } catch (err) {
      if (isApiError(err) && err.isConflict()) {
        setShowLockedDialog(true);
      } else {
        setSubmitError(err);
      }
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRetry(): Promise<void> {
    if (!retryRef.current) return;
    const { uidHpg, hpgValues } = retryRef.current;
    setShowPartialFailureDialog(false);
    setIsSaving(true);
    try {
      await doSaveComplementarios(uidHpg, hpgValues);
    } finally {
      setIsSaving(false);
    }
  }

  // ── Close / discard ───────────────────────────────────────────────────────

  function handleClose(): void {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
    } else {
      onClose();
    }
  }

  function handleConfirmDiscard(): void {
    setShowDiscardDialog(false);
    onClose();
  }

  // ── Loading / error states ────────────────────────────────────────────────

  const isLoadingContent =
    isEdit &&
    (hpgQuery.isLoading ||
      opgQuery.isLoading ||
      coproQuery.isLoading ||
      baermannQuery.isLoading ||
      sedFhQuery.isLoading);

  const isErrorContent =
    isEdit &&
    (hpgQuery.isError ||
      opgQuery.isError ||
      coproQuery.isError ||
      baermannQuery.isError ||
      sedFhQuery.isError);

  const showForm = !isLoadingContent && !isErrorContent;

  // ── Tab labels ────────────────────────────────────────────────────────────

  const datosLabel = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span>Datos del análisis</span>
      {tabDatosHasError && <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />}
    </Box>
  );

  const muestrasLabel = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <span>Muestras HPG</span>
      <Chip
        label={muestrasCount}
        size="small"
        sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
      />
      {tabMuestrasHasError && <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />}
    </Box>
  );

  function tabLabelComp(c: ComplementarioState, visibleIndex: number): React.ReactNode {
    const hasSumError = c.tipo === 'coprocultivo' && c.sumError;
    const hasRefError = erroresValidacion.some((e) => e.tabId === `comp-${c.localId}`);
    const hasError = hasSumError || hasRefError;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <span>
          {TIPO_LABEL[c.tipo]} {visibleIndex + 1}
        </span>
        {hasError && <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />}
      </Box>
    );
  }

  // ── Subtitle ──────────────────────────────────────────────────────────────

  const subtitleParts = [
    numeroProtocolo != null ? `Protocolo N° ${numeroProtocolo}` : null,
    establecimientoNombre ?? null,
  ].filter(Boolean);
  const subtitle = subtitleParts.length > 0 ? subtitleParts.join(' · ') : null;

  const dialogTitle = (() => {
    if (!isEdit) return 'Nuevo análisis HPG';
    const numFmt =
      numeroProtocolo != null
        ? formatNumeroAnalisis(numeroProtocolo, hpgQuery.data?.numero_analisis ?? null)
        : null;
    return numFmt ? `Editar análisis HPG ${numFmt}` : 'Editar análisis HPG';
  })();

  const hasPendingUploads = complementarios.some(
    (c): c is SedFhComplementarioState =>
      c.tipo === 'sedimentacion_fh' &&
      !c.marcadoParaEliminar &&
      c.fotosTemporales.some((f) => f.uploadStatus === 'uploading'),
  );

  const saveDisabled =
    isSaving || hasPendingUploads || (isEdit && !hpgIsDirty && !complementariosDirty);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        maxWidth="lg"
        fullWidth
        fullScreen={isSmall}
        onClose={handleClose}
      >
        {/* Title */}
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            pb: subtitle ? 1 : 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>{dialogTitle}</Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleClose} aria-label="Cerrar" size="small" sx={{ mt: 0.5 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Tabs */}
        {showForm && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabActiva}
              onChange={(_, v: string) => {
                if (v !== 'agregar') setTabActiva(v as TabId);
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ px: 3 }}
            >
              <Tab value="datos" label={datosLabel} />
              <Tab value="muestras" label={muestrasLabel} />

              {complementariosVisibles.flatMap((c, vi) => {
                const compTab = (
                  <Tab key={c.localId} value={`comp-${c.localId}`} label={tabLabelComp(c, vi)} />
                );
                if (c.tipo !== 'sedimentacion_fh') return [compTab];
                return [
                  compTab,
                  <Tab
                    key={`fotos-${c.localId}`}
                    value={`fotos-${c.localId}`}
                    label={`Fotos ${vi + 1}`}
                  />,
                ];
              })}

              <Tab
                value="agregar"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AddIcon sx={{ fontSize: 16 }} />
                    <span>Agregar</span>
                  </Box>
                }
                onClick={(e) => {
                  if (tipoComplementario === null) {
                    setMenuAnchor(e.currentTarget);
                  } else {
                    agregarNuevo(tipoComplementario);
                  }
                }}
              />
            </Tabs>

            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem onClick={() => agregarNuevo('opg')}>OPG</MenuItem>
              <MenuItem onClick={() => agregarNuevo('coprocultivo')}>Coprocultivo</MenuItem>
              <MenuItem onClick={() => agregarNuevo('baermann')}>Baermann</MenuItem>
              <MenuItem onClick={() => agregarNuevo('sedimentacion_fh')}>Sed. Fh</MenuItem>
            </Menu>
          </Box>
        )}

        {/* Draft recovery banner */}
        {showForm && pendingDraft && (
          <Box sx={{ px: 3, pt: 2, pb: 2 }}>
            <Alert
              severity="info"
              sx={{
                bgcolor: 'rgba(90, 28, 112, 0.10)',
                color: '#3d1350',
                '& .MuiAlert-icon': { color: '#5A1C70' },
              }}
              action={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button size="small" variant="outlined" onClick={() => void handleRestoreDraft()}>
                    Restaurar
                  </Button>
                  <Button size="small" color="inherit" onClick={handleDiscardDraft}>
                    Descartar
                  </Button>
                </Box>
              }
            >
              Tenés un borrador guardado{' '}
              {formatDistanceToNow(new Date(pendingDraft.timestamp), {
                addSuffix: true,
                locale: es,
              })}.
            </Alert>
          </Box>
        )}

        {/* Validation error alert — fixed, above scrollable content */}
        {showForm && erroresValidacion.length > 0 && (
          <Box sx={{ px: 3, pt: 2, pb: 0 }}>
            <Alert
              severity="error"
              sx={{ '& .MuiAlert-message': { width: '100%' } }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                Hay {erroresValidacion.length} problema{erroresValidacion.length !== 1 ? 's' : ''} que tenés que resolver antes de guardar:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {erroresValidacion.map((err, i) => (
                  <Box
                    component="li"
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2">{err.mensaje}</Typography>
                    <Button
                      size="small"
                      sx={{ flexShrink: 0, minWidth: 'auto', py: 0 }}
                      onClick={() => setTabActiva(err.tabId as TabId)}
                    >
                      Ir
                    </Button>
                  </Box>
                ))}
              </Box>
            </Alert>
          </Box>
        )}

        {/* Content */}
        <DialogContent dividers sx={{ px: 3, py: 1 }}>
          {isLoadingContent && (
            <Box>
              <Skeleton variant="rounded" height={60} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={280} sx={{ mb: 3 }} />
              <Skeleton variant="rounded" height={200} />
            </Box>
          )}

          {isErrorContent && (
            <Alert severity="error">
              {getErrorMessage(hpgQuery.error ?? opgQuery.error ?? coproQuery.error ?? baermannQuery.error ?? sedFhQuery.error)}
            </Alert>
          )}

          {showForm && (
            <>
              {!!submitError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {getErrorMessage(submitError)}
                </Alert>
              )}

              {tabActiva === 'datos' && <AnalisisDatosGeneralesForm noPaper />}

              {tabActiva === 'muestras' && (
                <>
                  {minMuestrasError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      Cargá al menos una muestra.
                    </Alert>
                  )}
                  <HpgFormMuestrasTable noPaper />
                </>
              )}

              {complementariosVisibles
                .filter((c): c is SedFhComplementarioState => c.tipo === 'sedimentacion_fh')
                .map((c) => {
                  if (tabActiva !== `fotos-${c.localId}`) return null;
                  const existingFotos =
                    sedFhQuery.data?.find((s) => s.uid_analisis === c.uidAnalisis)?.fotos ?? [];
                  return (
                    <Paper key={c.localId} variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Fotos del análisis
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Adjuntá imágenes de los huevos identificados (opcional). Podés vincular cada
                        foto a un pool por el caption.
                      </Typography>
                      <FotosManager
                        fotos={existingFotos}
                        fotosTemporales={c.fotosTemporales}
                        onFotosTemporalesChange={(action) => {
                          setComplementarios((prev) =>
                            prev.map((comp) => {
                              if (comp.localId !== c.localId) return comp;
                              const currentFotos = (comp as SedFhComplementarioState).fotosTemporales;
                              const newFotos =
                                typeof action === 'function' ? action(currentFotos) : action;
                              // No marcamos isDirty: las fotos se persisten al instante (modo edición)
                              // o van con el create (comp nuevo, ya dirty por ser nuevo).
                              return { ...comp, fotosTemporales: newFotos } as ComplementarioState;
                            }),
                          );
                        }}
                        uidProtocolo={uidProtocolo}
                        uidAnalisis={c.uidAnalisis}
                        useUploadFoto={useUploadFotoSedimentacionFh}
                        useEditarCaption={useEditarCaptionFotoSedimentacionFh}
                        useEliminarFoto={useEliminarFotoSedimentacionFh}
                        disabled={isSaving || sedFhQuery.isFetching}
                      />
                    </Paper>
                  );
                })}

              {complementariosVisibles.map((c) => {
                if (tabActiva !== `comp-${c.localId}`) return null;

                if (c.tipo === 'opg') {
                  return (
                    <HpgDialogOpgTab
                      key={c.localId}
                      muestras={c.muestras}
                      posicionesHpgValidas={posicionesHpgValidas}
                      onChange={(muestras) => patchComp(c.localId, { muestras, isDirty: true })}
                      onEliminarTab={() => setConfirmDeleteComp(c.localId)}
                    />
                  );
                }

                if (c.tipo === 'coprocultivo') {
                  return (
                    <HpgDialogCoprocultivoTab
                      key={c.localId}
                      state={c}
                      posicionesHpgValidas={posicionesHpgValidas}
                      onChange={(patch) => patchComp(c.localId, { ...patch, isDirty: true })}
                      onEliminarTab={() => setConfirmDeleteComp(c.localId)}
                    />
                  );
                }

                if (c.tipo === 'baermann') {
                  return (
                    <HpgDialogBaermannTab
                      key={c.localId}
                      state={c}
                      posicionesHpgValidas={posicionesHpgValidas}
                      onChange={(patch) => patchComp(c.localId, { ...patch, isDirty: true })}
                      onEliminarTab={() => setConfirmDeleteComp(c.localId)}
                      showErrors={hasAttemptedValidacion}
                      disabled={isSaving}
                    />
                  );
                }

                if (c.tipo === 'sedimentacion_fh') {
                  return (
                    <HpgDialogSedFhTab
                      key={c.localId}
                      state={c}
                      posicionesHpgValidas={posicionesHpgValidas}
                      onChange={(patch) => patchComp(c.localId, { ...patch, isDirty: true })}
                      onEliminarTab={() => setConfirmDeleteComp(c.localId)}
                      showErrors={hasAttemptedValidacion}
                      disabled={isSaving}
                    />
                  );
                }

                return null;
              })}
            </>
          )}
        </DialogContent>

        {/* Actions */}
        <DialogActions>
          <Button variant="outlined" onClick={handleClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={saveDisabled}
            onClick={() => void handleSubmit(handleSave, handleSaveError)()}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {hasPendingUploads ? 'Subiendo fotos...' : isSaving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discard confirmation */}
      <Dialog open={showDiscardDialog} maxWidth="xs" onClose={() => setShowDiscardDialog(false)}>
        <DialogTitle>Descartar cambios</DialogTitle>
        <DialogContent>
          <DialogContentText>¿Descartar los cambios sin guardar?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiscardDialog(false)}>Seguir editando</Button>
          <Button color="error" onClick={handleConfirmDiscard}>
            Descartar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Protocolo cerrado */}
      <Dialog open={showLockedDialog} maxWidth="xs">
        <DialogTitle>Protocolo cerrado</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Este protocolo está cerrado y ya no admite cambios.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowLockedDialog(false); onClose(); }}>Aceptar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar eliminar complementario */}
      <Dialog open={!!confirmDeleteComp} maxWidth="xs" onClose={() => setConfirmDeleteComp(null)}>
        <DialogTitle>Eliminar complementario</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {(() => {
              const comp = complementarios.find((c) => c.localId === confirmDeleteComp);
              if (!comp) return '';
              const label = TIPO_LABEL[comp.tipo];
              return comp.uidAnalisis
                ? `¿Eliminar este ${label}? Se borrará al guardar.`
                : `¿Eliminar este ${label}?`;
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteComp(null)}>Cancelar</Button>
          <Button color="error" onClick={handleConfirmEliminarComp}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error parcial en complementarios */}
      <Dialog open={showPartialFailureDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Error al guardar complementarios</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            El HPG se guardó correctamente, pero algunos complementarios fallaron:
          </DialogContentText>
          {partialFailureErrors.map((msg, i) => (
            <Alert key={i} severity="error" sx={{ mb: 1 }}>
              {msg}
            </Alert>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowPartialFailureDialog(false);
              notify.success('HPG guardado. Revisá los complementarios que fallaron.');
              onSuccess?.();
              onClose();
            }}
          >
            Mantener solo HPG y cerrar
          </Button>
          <Button variant="contained" onClick={() => void handleRetry()}>
            Reintentar
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
